import { useEffect, useMemo, useRef, useState } from 'react';
import {
  exportReportForWeb,
  fetchFmsReportData,
  fetchTicketReportData,
  fetchAttendanceReportData
} from '@/features/reports/api';
import { useAuth } from '@/features/auth/AuthProvider';

// Removed rangeBounds and rangeOptions as user requested direct Start/End dates

const initialTicketFilters = { priority: '', user: '', status: '', search: '' };
const initialFmsFilters = { status: '', search: '' };

function toYmd(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseMaybeStringPayload(payload) {
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function includesText(parts, query) {
  if (!query) return true;
  const haystack = parts
    .map((part) => String(part ?? '').trim().toLowerCase())
    .filter(Boolean)
    .join(' ');
  return haystack.includes(query);
}

function mimeTypeForFormat(format) {
  if (format === 'pdf') return 'application/pdf';
  if (format === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  return 'text/csv';
}

function extensionForFormat(format) {
  if (format === 'pdf') return 'pdf';
  if (format === 'xlsx') return 'xlsx';
  return 'csv';
}

export function useReportsData() {
  const { user } = useAuth();
  const employeeId = user?.['Employee ID'] || '';
  const role = user?.Role || 'User';
  const normalizedRole = String(role || '').trim().toLowerCase();
  const isManagerOnly = normalizedRole === 'manager' || normalizedRole === 'team leader' || normalizedRole === 'tl';
  const [activeTab, setActiveTab] = useState(isManagerOnly ? 'tickets' : 'management');
  
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date();
    return toYmd(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [customEnd, setCustomEnd] = useState(() => toYmd(new Date()));
  const [ticketFilters, setTicketFilters] = useState(initialTicketFilters);
  const [fmsFilters, setFmsFilters] = useState(initialFmsFilters);
  const [attendanceFilters, setAttendanceFilters] = useState({ employees: [] }); // array of employee IDs
  const [state, setState] = useState({
    loading: true,
    error: null,
    tickets: [],
    fms: [],
    attendance: [],
    attendanceUsers: [],
    leaves: [],
    intimations: []
  });
  const [message, setMessage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const refreshIndex = useRef(0);

  useEffect(() => {
    if (isManagerOnly && activeTab === 'management') {
      setActiveTab('tickets');
    }
  }, [activeTab, isManagerOnly]);

  const bounds = useMemo(
    () => ({ startDate: customStart || '', endDate: customEnd || '' }),
    [customStart, customEnd]
  );
  const rangeKey = `${bounds.startDate}:${bounds.endDate}`;
  const loadedRanges = useRef({ tickets: '', fms: '', attendance: '' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const prevRangeKey = useRef(rangeKey);
  if (prevRangeKey.current !== rangeKey) {
    prevRangeKey.current = rangeKey;
    loadedRanges.current = { tickets: '', fms: '', attendance: '' };
  }

  useEffect(() => {
    if (!employeeId) {
      setState({
        loading: false,
        error: null,
        tickets: [],
        fms: [],
        attendance: [],
        attendanceUsers: [],
        leaves: [],
        intimations: []
      });
      return undefined;
    }

    let alive = true;

    if (!isManagerOnly && activeTab === 'management') {
      setState((cur) => ({ ...cur, loading: false, error: null }));
      return undefined;
    }

    if (activeTab === 'tickets' && loadedRanges.current.tickets === rangeKey) {
      setState((cur) => ({ ...cur, loading: false }));
      return undefined;
    }
    if (activeTab === 'fms' && loadedRanges.current.fms === rangeKey) {
      setState((cur) => ({ ...cur, loading: false }));
      return undefined;
    }
    if (activeTab === 'attendance' && loadedRanges.current.attendance === rangeKey) {
      setState((cur) => ({ ...cur, loading: false }));
      return undefined;
    }

    async function loadActiveTab() {
      setState((current) => ({ ...current, loading: true, error: null }));
      setMessage(null);
      try {
        if (activeTab === 'tickets') {
          const raw = await fetchTicketReportData(employeeId, bounds.startDate, bounds.endDate);
          if (!alive) return;
          const payload = parseMaybeStringPayload(raw);
          loadedRanges.current.tickets = rangeKey;
          setState((cur) => ({
            ...cur,
            loading: false,
            error: null,
            tickets: safeArray(payload?.data)
          }));
        } else if (activeTab === 'fms') {
          const raw = await fetchFmsReportData(employeeId, role, bounds.startDate, bounds.endDate);
          if (!alive) return;
          const payload = parseMaybeStringPayload(raw);
          loadedRanges.current.fms = rangeKey;
          setState((cur) => ({
            ...cur,
            loading: false,
            error: null,
            fms: safeArray(payload?.data)
          }));
        } else if (activeTab === 'attendance') {
          const raw = await fetchAttendanceReportData(bounds.startDate, bounds.endDate);
          if (!alive) return;
          const payload = parseMaybeStringPayload(raw);
          loadedRanges.current.attendance = rangeKey;
          setState((cur) => ({
            ...cur,
            loading: false,
            error: null,
            attendance: safeArray(payload?.data),
            attendanceUsers: safeArray(payload?.users),
            leaves: safeArray(payload?.leaves),
            intimations: safeArray(payload?.intimations)
          }));
        } else {
          if (!alive) return;
          setState((cur) => ({ ...cur, loading: false }));
        }
      } catch (error) {
        if (!alive) return;
        setState((cur) => ({
          ...cur,
          loading: false,
          error: error.message || 'Failed to load report data.'
        }));
      }
    }

    loadActiveTab();
    return () => {
      alive = false;
    };
  }, [employeeId, role, activeTab, isManagerOnly, rangeKey, refreshTrigger]);

  const ticketPriorities = useMemo(
    () =>
      Array.from(new Set(state.tickets.map((item) => item.Priority).filter(Boolean))).sort((left, right) =>
        left.localeCompare(right)
      ),
    [state.tickets]
  );

  const ticketUsers = useMemo(
    () =>
      Array.from(
        new Set(state.tickets.map((item) => item['Employee Name'] || item.User).filter(Boolean))
      ).sort((left, right) => left.localeCompare(right)),
    [state.tickets]
  );

  const ticketStatuses = useMemo(
    () =>
      Array.from(new Set(state.tickets.map((item) => item.Status).filter(Boolean))).sort((left, right) =>
        left.localeCompare(right)
      ),
    [state.tickets]
  );

  const fmsStatuses = useMemo(
    () =>
      Array.from(new Set(state.fms.map((item) => item.Status).filter(Boolean))).sort((left, right) =>
        left.localeCompare(right)
      ),
    [state.fms]
  );

  const filteredTickets = useMemo(() => {
    const search = String(ticketFilters.search || '').trim().toLowerCase();
    return state.tickets.filter((item) => {
      const matchesPriority = !ticketFilters.priority || item.Priority === ticketFilters.priority;
      const user = item['Employee Name'] || item.User || '';
      const matchesUser = !ticketFilters.user || user === ticketFilters.user;
      const matchesStatus = !ticketFilters.status || item.Status === ticketFilters.status;
      const matchesSearch = includesText(
        [
          item['Ticket ID'],
          item.Name,
          item['Employee Name'],
          item['Task Description'],
          item.Description,
          item.Priority,
          item.Status,
          item['Task Category'] || item.Category || ''
        ],
        search
      );
      return matchesPriority && matchesUser && matchesStatus && matchesSearch;
    });
  }, [state.tickets, ticketFilters]);

  const filteredFms = useMemo(() => {
    const search = String(fmsFilters.search || '').trim().toLowerCase();
    return state.fms.filter((item) => {
      const matchesStatus = !fmsFilters.status || item.Status === fmsFilters.status;
      const matchesSearch = includesText(
        [
          item['FMS Name'],
          item.Name,
          item['Task Name'],
          item['Task Description'],
          item.Description,
          item.Who,
          item['Assigned To'],
          item.Status
        ],
        search
      );
      return matchesStatus && matchesSearch;
    });
  }, [state.fms, fmsFilters]);

  const filteredAttendance = useMemo(() => {
    return state.attendance.filter((item) => {
      // If no employees selected, show all available to the reviewer
      if (!attendanceFilters.employees || attendanceFilters.employees.length === 0) return true;
      return attendanceFilters.employees.includes(item.employeeId);
    });
  }, [state.attendance, attendanceFilters]);

  const filteredLeaves = useMemo(() => {
    return (state.leaves || []).filter((item) => {
      if (!attendanceFilters.employees || attendanceFilters.employees.length === 0) return true;
      const empId = item['Employee ID'] || item.employeeId || item.EmpID;
      return attendanceFilters.employees.includes(empId);
    });
  }, [state.leaves, attendanceFilters]);

  const filteredIntimations = useMemo(() => {
    return (state.intimations || []).filter((item) => {
      if (!attendanceFilters.employees || attendanceFilters.employees.length === 0) return true;
      const empId = item['Employee ID'] || item.employeeId || item.EmpID;
      return attendanceFilters.employees.includes(empId);
    });
  }, [state.intimations, attendanceFilters]);

  function refresh() {
    loadedRanges.current = { tickets: '', fms: '', attendance: '' };
    setRefreshTrigger((c) => c + 1);
  }

  function resetTicketFilters() {
    setTicketFilters(initialTicketFilters);
  }

  function resetFmsFilters() {
    setFmsFilters(initialFmsFilters);
  }

  function resetAttendanceFilters() {
    setAttendanceFilters({ employees: [] });
  }

  async function download(format) {
    setDownloading(true);
    setDownloadingFormat(format);
    setMessage(null);
    try {
      let sheetName = 'Report';
      let filters = {};

      if (activeTab === 'tickets') {
        sheetName = 'Tickets_Report';
        filters = { ...ticketFilters };
      } else if (activeTab === 'fms') {
        sheetName = 'FMS_Report';
        filters = { ...fmsFilters };
      } else if (activeTab === 'attendance') {
        sheetName = 'Attendance_Report';
        filters = { ...attendanceFilters };
      } else if (activeTab === 'project') {
        sheetName = 'Project_Report';
        filters = { scope: 'project' };
      } else if (activeTab === 'management') {
        sheetName = 'Management_Report';
        filters = { scope: 'management' };
      }

      const payload = await exportReportForWeb(
        format,
        sheetName,
        employeeId,
        role,
        bounds.startDate,
        bounds.endDate,
        filters
      );

      if (!payload?.success || !payload?.base64Data) {
        throw new Error(payload?.message || 'Report export failed.');
      }

      if (typeof window !== 'undefined') {
        const binaryString = window.atob(payload.base64Data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mimeTypeForFormat(format) });
        const url = URL.createObjectURL(blob);
        
        const link = window.document.createElement('a');
        link.href = url;
        link.download = payload.fileName || `worktrack-report.${extensionForFormat(format)}`;
        link.click();
        
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }

      const reportName = activeTab === 'tickets' ? 'Ticket' : activeTab === 'fms' ? 'FMS' : activeTab === 'attendance' ? 'Attendance' : activeTab === 'project' ? 'Project' : 'Management';
      setMessage({
        tone: 'success',
        text: `${reportName} report exported successfully.`
      });
      return payload;
    } catch (error) {
      const text = error.message || 'Report export failed.';
      setMessage({ tone: 'danger', text });
      return { success: false, message: text };
    } finally {
      setDownloading(false);
      setDownloadingFormat(null);
    }
  }

  return {
    employeeId,
    currentUser: user || null,
    role,
    isManagerOnly,
    activeTab,
    setActiveTab,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    bounds,
    loading: state.loading,
    error: state.error, clearError: () => setState((current) => ({ ...current, error: null })),
    message, clearMessage: () => setMessage(null),
    downloading,
    downloadingFormat,
    tickets: filteredTickets,
    ticketStatuses,
    ticketPriorities,
    ticketUsers,
    ticketFilters,
    setTicketFilters,
    resetTicketFilters,
    fms: filteredFms,
    fmsStatuses,
    fmsFilters,
    setFmsFilters,
    resetFmsFilters,
    attendance: filteredAttendance,
    attendanceUsers: state.attendanceUsers,
    leaves: filteredLeaves,
    intimations: filteredIntimations,
    attendanceFilters,
    setAttendanceFilters,
    resetAttendanceFilters,
    refresh,
    download
  };
}
